// How an import from Google Drive or OneDrive ended: what came in, what didn't and why, and what was left out
// before it started. A file that didn't come in stays in its folder as failed until it is deleted,
// which frees its name for the next try, so the dialog says so, and offers to pick again.
//
// What was left out is only known to the tab that started the import, so an import picked up after
// a reload reports what it took on and nothing more.
import PropTypes from 'prop-types'

import Dialog from '@mui/material/Dialog'
import ImportIcon from '@mui/icons-material/AddToDriveOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import './importDialog.scss'

const PROVIDER_NAMES = {google: 'Google Drive', onedrive: 'OneDrive'}

// Why a file the import took on didn't come in.
const FAILURES = {
  rate_limited: 'Asked to slow down',
  token_expired: 'Ran out of time',
  not_found: 'No longer available',
  export_too_large: 'Too large to export',
  provider_error: 'Something went wrong',
  virus: 'Blocked by the virus scan'
}

// Why a file was left out before the import started.
const LEFT_OUT = {
  already_imported: 'Already imported',
  unsupported: 'Can’t be imported',
  unavailable: 'Not available'
}

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`

const FileList = ({heading, files}) => (
  <section className='results-group'>
    <h3 className='results-heading'>{heading}</h3>
    <ul className='results-list'>
      {files.map(({key, name, why}) => (
        <li key={key} className='results-file'>
          <span className='results-name'>{name}</span>
          {why && <span className='results-why'>{why}</span>}
        </li>
      ))}
    </ul>
  </section>
)

FileList.propTypes = {
  heading: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    why: PropTypes.string
  })).isRequired
}

const summaryOf = (imported, failed, leftOut) => {
  if (!failed.length && !leftOut.length) return `${plural(imported.length, 'file')} imported.`
  if (!imported.length) return 'Nothing was imported.'

  return `${imported.length} of ${plural(imported.length + failed.length + leftOut.length, 'file')} imported.`
}

const ImportResultsDialog = ({provider, files, skipped, onClose, onPickAgain}) => {
  const name = PROVIDER_NAMES[provider]
  const imported = files.filter(({status}) => status === 'imported')
  const failed = files.filter(({status}) => status === 'failed')
  const throttled = failed.some(({failure}) => failure === 'rate_limited')

  return (
    <Dialog open onClose={onClose} aria-labelledby='import-results-title' slotProps={{paper: {className: 'import-dialog'}}}>
      <div className='dialog-head'>
        <ImportIcon />
        <h2 className='dialog-title' id='import-results-title'>Import from {name}</h2>
      </div>
      <Text className='dialog-body'>{summaryOf(imported, failed, skipped)}</Text>

      <div className='results-groups'>
        {failed.length > 0 && (
          <FileList
            heading='Not imported'
            files={failed.map(({uploadId, filename, failure}) => ({key: uploadId, name: filename, why: FAILURES[failure]}))}
          />
        )}
        {skipped.length > 0 && (
          <FileList
            heading='Left out'
            files={skipped.map(({fileId, filename, reason}) => ({key: fileId, name: filename, why: LEFT_OUT[reason]}))}
          />
        )}
        {imported.length > 0 && (
          <FileList heading='Imported' files={imported.map(({uploadId, filename}) => ({key: uploadId, name: filename}))} />
        )}
      </div>

      {failed.length > 0 && (
        <Text className='dialog-note'>
          Files that weren’t imported stay in their folder, marked failed. Delete them before you pick them again.
          {throttled && ` ${name} is busy, so wait a few minutes first.`}
        </Text>
      )}

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Close</BaseButton>
        {(failed.length > 0 || skipped.length > 0) && (
          <BaseButton className='dialog-confirm' variant='text' onClick={onPickAgain}>Pick again</BaseButton>
        )}
      </div>
    </Dialog>
  )
}

ImportResultsDialog.propTypes = {
  provider: PropTypes.oneOf(Object.keys(PROVIDER_NAMES)).isRequired,
  files: PropTypes.arrayOf(PropTypes.shape({
    uploadId: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    failure: PropTypes.string
  })).isRequired,
  skipped: PropTypes.arrayOf(PropTypes.shape({
    fileId: PropTypes.string.isRequired,
    filename: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  onPickAgain: PropTypes.func.isRequired
}

export default ImportResultsDialog
